/**
 * Fotos vinculadas à atividade — as Memórias filtradas por `activityId`.
 *
 * Mostra até quatro miniaturas (a capa marcada) e um "+N". Tirar foto abre a
 * câmera de trás (`capture="environment"`); "Da galeria" aceita várias. As
 * duas caem na MESMA fila das Memórias, já com dia e atividade: a foto fica
 * na atividade e no álbum do dia ao mesmo tempo — é um registro só. Enquanto
 * sobe, a miniatura aparece com véu; erro tem "tentar de novo" na fila.
 * Sem storage no ambiente, não aparece.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useDiasDaViagem } from '../../trip/dias';
import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { SectionLabel } from '../../ui/primitives';
import { memoriasApi } from '../memorias/api';
import { useAtualizarAoEnviar, useConfigMedia } from '../memorias/comum';
import { resumoDaFila, useFila } from '../memorias/fila';

export function FotosDaAtividade({ dia, activityId, coverMediaId }: { dia: string; activityId: string; coverMediaId?: string | null | undefined }) {
  const { tripId } = useTrip();
  const config = useConfigMedia();
  const dias = useDiasDaViagem(tripId);
  const dayId = dias.days?.find((d) => d.date === dia)?.id ?? null;
  const fotos = useQuery({
    queryKey: ['memorias', tripId, 'atividade', activityId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/trips/${tripId}/media?activityId=${activityId}&limit=24`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Falha (${res.status})`);
      return (await res.json()) as Awaited<ReturnType<typeof memoriasApi.listar>>;
    },
    enabled: config.data?.media === true,
    staleTime: 30_000,
  });
  useAtualizarAoEnviar(tripId);
  const adicionar = useFila((s) => s.adicionar);
  const tentarDeNovo = useFila((s) => s.tentarDeNovo);
  const todosOsItens = useFila((s) => s.itens);
  const daAtividade = useMemo(() => todosOsItens.filter((i) => i.tripId === tripId && i.activityId === activityId && i.estado !== 'concluida'), [todosOsItens, tripId, activityId]);
  const andamento = useMemo(() => resumoDaFila(daAtividade), [daAtividade]);
  const camera = useRef<HTMLInputElement>(null);
  const galeria = useRef<HTMLInputElement>(null);

  if (config.data?.media !== true) return null;
  const itens = fotos.data?.items ?? [];
  const visiveis = itens.slice(0, itens.length > 4 ? 3 : 4);

  function escolher(lista: FileList | null) {
    if (!lista || !dayId) return;
    adicionar([...lista], { tripId, dayId, activityId });
    if (camera.current) camera.current.value = '';
    if (galeria.current) galeria.current.value = '';
  }

  return (
    <>
      <SectionLabel>Fotos da atividade</SectionLabel>
      {(visiveis.length > 0 || daAtividade.length > 0) && (
        <div className="ativ-fotos" data-testid="ativ-fotos">
          {daAtividade.map((i) => (
            <span key={i.id} className={`ativ-fotos__subindo ativ-fotos__subindo--${i.estado}`} title={i.estado === 'erro' ? 'Falhou — tentar de novo' : 'Enviando…'}>
              <img src={i.previaUrl} alt="" />
              {i.estado === 'erro' ? (
                <button type="button" className="ativ-fotos__retry" onClick={() => tentarDeNovo(i.id)} aria-label="Tentar enviar de novo">
                  <Icon name="sincronizar" size={16} />
                </button>
              ) : (
                <span className="ativ-fotos__veu" aria-hidden="true" />
              )}
            </span>
          ))}
          {visiveis.map((f) => (
            <Link key={f.id} to={`/memorias/${dia}/${f.id}`} aria-label={`Foto de ${f.uploader.name}${f.id === coverMediaId ? ' (capa)' : ''}`} className={f.id === coverMediaId ? 'ativ-fotos__capa' : ''} data-testid={f.id === coverMediaId ? 'ativ-foto-capa' : 'ativ-foto'}>
              <img src={f.thumbUrl} alt="" loading="lazy" />
              {f.id === coverMediaId && <span className="ativ-fotos__selo">Capa</span>}
            </Link>
          ))}
          {itens.length > 4 && (
            <Link to={`/memorias/${dia}`} className="ativ-fotos__mais">
              +{itens.length - 3}
            </Link>
          )}
        </div>
      )}
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden data-testid="ativ-camera" onChange={(e) => escolher(e.target.files)} />
      <input ref={galeria} type="file" accept="image/*" multiple hidden data-testid="ativ-galeria" onChange={(e) => escolher(e.target.files)} />
      <div className="chip-row">
        <button type="button" className="nv-btn nv-btn--chip" onClick={() => camera.current?.click()} disabled={!dayId} data-testid="ativ-tirar-foto">
          <Icon name="memorias" size={16} /> Tirar foto
        </button>
        <button type="button" className="nv-btn nv-btn--chip" onClick={() => galeria.current?.click()} disabled={!dayId} data-testid="ativ-da-galeria">
          <Icon name="criar" size={16} /> Da galeria
        </button>
        {andamento.ativas > 0 && <span className="auth-note ativ-fotos__status">Enviando {andamento.ativas}…</span>}
        {andamento.erros > 0 && <span className="auth-error ativ-fotos__status">{andamento.erros} falhou</span>}
      </div>
    </>
  );
}
